import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography } from '@mui/material';
import '../../../assets/css/GoogleMapsPopUp.css';

export interface MapPopUpProps {
  /** When true, shows a close control in the top-right (default false). */
  showCloseButton?: boolean;
  /** Invoked when the close control is activated (only shown when `showCloseButton` is true). */
  onClose?: () => void;
  /** Data object containing popup information */
  data: {
    name?: string;
    category?: string;
    distance?: number | null;
    travelTime?: number | null;
    // Asset-specific fields
    sector?: string;
    dealName?: string;
    dealId?: number;
    acquisitionPrice?: number;
    valuationAmount?: number;
    totalArea?: number;
    areaType?: string;
    currency?: string;
  };
}

export const MapPopUp: React.FC<MapPopUpProps> = ({
  data,
  showCloseButton = false,
  onClose,
}) => {
  // Determine if this is an asset or other type
  const isAsset = data.category === 'Asset';
  const isTrainStation = data.category === 'Train station';
  const isAirport = data.category === 'Airport';
  const useLightTheme = isAsset || isTrainStation || isAirport;

  // Get display name and category
  const displayName = data.name;
  const displayCategory = data.category;

  // Format currency
  const formatCurrency = (amount: number | undefined, currency: string | undefined): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return '-';
    const curr = currency || 'GBP';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Get acquisition price or valuation
  const getPriceOrValuation = (): { label: string; value: number | undefined; currency: string | undefined } => {
    if (data.acquisitionPrice !== undefined && data.acquisitionPrice !== null) {
      return { label: 'Acquisition Price', value: data.acquisitionPrice, currency: data.currency };
    }
    if (data.valuationAmount !== undefined && data.valuationAmount !== null) {
      return { label: 'Valuation', value: data.valuationAmount, currency: data.currency };
    }
    return { label: '', value: undefined, currency: undefined };
  };

  const priceInfo = getPriceOrValuation();
  const pricePerAreaValue = priceInfo.value && data.totalArea && data.totalArea > 0
    ? priceInfo.value / data.totalArea
    : null;

  return (
    <Box            
      className='map-info-top-box'
      sx={{
        color: useLightTheme ? '#182737' : '#FFF',
        backgroundColor: useLightTheme ? '#FFF' : '#182737',
        borderRadius: '8px',
        minWidth: '200px',
      }}
    >
      {showCloseButton ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            columnGap: 1,
            rowGap: 0,
            /* Match DealCompMapPopUp: first row + close inside padded column (map-info-values-box-wrapper uses 6px 12px). */
            pt: '6px',
            px: '12px',
            pb: 0,
          }}
        >
          <Box
            className="map-info-name-box"
            sx={{
              flex: 1,
              minWidth: 0,
              padding: '0 !important',
            }}
          >
            <Typography
              className="map-info-title"
              sx={{
                color: useLightTheme ? '#182737' : '#FFF',
              }}
            >
              {displayName}
            </Typography>

            {displayCategory && (
              <Typography
                className="map-info-sub-title"
                sx={{
                  color: useLightTheme ? '#6B7886' : 'rgba(255, 255, 255, 0.8)',
                }}
              >
                {displayCategory}
              </Typography>
            )}
          </Box>
          <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
            <IconButton
              type="button"
              size="small"
              aria-label="Close popup"
              disableRipple
              disableFocusRipple
              onClick={(e) => {
                e.stopPropagation();
                onClose?.();
              }}
              sx={{
                color: 'text.secondary',
                bgcolor: 'rgba(255,255,255,0.92)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
                outline: 'none',
                boxShadow: 'none',
                '&:focus': { outline: 'none' },
                '&.Mui-focusVisible': {
                  bgcolor: 'rgba(255,255,255,0.92)',
                },
                padding: 0,
              }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box className="map-info-name-box">
          <Typography
            className="map-info-title"
            sx={{
              color: useLightTheme ? '#182737' : '#FFF',
            }}
          >
            {displayName}
          </Typography>

          {displayCategory && (
            <Typography
              className="map-info-sub-title"
              sx={{
                color: useLightTheme ? '#6B7886' : 'rgba(255, 255, 255, 0.8)',
              }}
            >
              {displayCategory}
            </Typography>
          )}
        </Box>
      )}

      {/* Show asset-specific details */}
      {isAsset && (data.sector || data.dealName || priceInfo.value || pricePerAreaValue !== null) && (
        <Box className='map-info-values-box-wrapper'>
          {data.sector && (
            <Box>
              <Typography
                className='map-info-title'
                sx={{
                  color: isAsset ? '#182737' : '#FFF',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {data.sector}
              </Typography>
              <Typography
                className='map-info-sub-title'
                sx={{
                  color: isAsset ? '#6B7886' : 'rgba(255, 255, 255, 0.8)',
                  marginBottom: '0px',
                }}
              >
                Sector
              </Typography>
            </Box>
          )}
          {data.dealName && (
            <Box>
              {data.dealId ? (
                <a
                  href={`/deals/${data.dealId}`}
                  style={{
                    color: isAsset ? "#1B7A63" : "#FFF",
                    textDecoration: 'underline',
                    fontSize: '14px',
                    fontWeight: 600,
                    display: 'block',
                  }}
                >
                  {data.dealName}
                </a>
              ) : (
                <Typography
                  className='map-info-title'
                  sx={{
                    color: isAsset ? '#182737' : '#FFF',
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {data.dealName}
                </Typography>
              )}
              <Typography
                className='map-info-sub-title'
                sx={{
                  color: isAsset ? '#6B7886' : 'rgba(255, 255, 255, 0.8)',
                  marginBottom: '1px',
                }}
              >
                Deal
              </Typography>
            </Box>
          )}
          {priceInfo.value !== undefined && (
            <Box>
              <Typography
                className='map-info-title'
                sx={{
                  color: isAsset ? '#182737' : '#FFF',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {formatCurrency(priceInfo.value, priceInfo.currency)}
              </Typography>
              <Typography
                className='map-info-sub-title'
                sx={{
                  color: isAsset ? '#6B7886' : 'rgba(255, 255, 255, 0.8)',
                  marginBottom: '1px',
                }}
              >
                {priceInfo.label}
              </Typography>
            </Box>
          )}
          {pricePerAreaValue !== null && (
            <Box>
              <Typography
                className='map-info-title'
                sx={{
                  color: isAsset ? '#182737' : '#FFF',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                {formatCurrency(pricePerAreaValue, priceInfo.currency)} / {data.areaType || 'unit'}
              </Typography>
              <Typography
                className='map-info-sub-title'
                sx={{
                  color: isAsset ? '#6B7886' : 'rgba(255, 255, 255, 0.8)',
                  marginBottom: '1px',
                }}
              >
                Price / Area
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Show additional details if available (for non-assets) */}
      {!isAsset && (data.distance || data.travelTime) && (
        <Box className='map-info-values-box-wrapper'>
          {data.distance && (
            <Typography className='map-info-values'>
              Distance: {data.distance}m
            </Typography>
          )}
          {data.travelTime && (
            <Typography className='map-info-values'>
              Travel Time: {data.travelTime} min
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
